import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import { useEffect, useMemo, useState } from 'react';
import { DateValueType, formatDate } from '@lib/utils.ts';
import {
  TReportBlacklistUsersParams,
  TReportUsersWithExtraSession,
} from '@src/types';
import { getUsersWithExtraSession } from '@src/services/reports.ts';
import { useNavigate } from 'react-router-dom';

export const UsersWithExtraSessions = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [data, setData] = useState<TReportUsersWithExtraSession[]>([]);

  const navigate = useNavigate();

  const fetchUsersWithExtraSession = async (
    params?: TReportBlacklistUsersParams
  ) => {
    const res = await getUsersWithExtraSession(params);

    setData(res);
  };

  useEffect(() => {
    fetchUsersWithExtraSession().then();
  }, []);

  const handleGetData = async () => {
    await fetchUsersWithExtraSession({
      start_date: valueStart?.startDate?.toISOString(),
      end_date: valueEnd?.startDate?.toISOString(),
    });
  };

  const renderLevel = (level?: boolean, depth?: boolean) => {
    if (!level) {
      return (
        <p
          className={
            'bg-[#ddd] py-[2px] px-1 text-[#ababab] rounded-[3px] w-fit'
          }
        >
          N/A
        </p>
      );
    }
    if (depth) {
      return (
        <p className={'bg-lime py-[2px] px-1 rounded-[3px] w-fit'}>LVL2</p>
      );
    }

    return <p className={'bg-lime py-[2px] px-1 rounded-[3px] w-fit'}>LVL1</p>;
  };

  const columns = useMemo(
    (): TColumn<TReportUsersWithExtraSession>[] => [
      {
        title: 'Username',
        render: (item) => <p>{item?.username}</p>,
      },
      {
        title: 'Name',
        render: (item) => (
          <p>
            {item?.user.first_name} {item?.user.last_name}
          </p>
        ),
      },
      {
        title: 'Email',
        render: (item) => <p>{item?.user.email}</p>,
      },
      {
        title: 'Expiry',
        render: (item) => <p>{formatDate(item?.expire_date)}</p>,
      },
      {
        title: 'Accounts',
        render: (item) => (
          <p>
            Active: {item?.active ?? 0} | Closed: {item?.closed ?? 0}
          </p>
        ),
      },
      {
        title: 'CBOT',
        render: (item) => renderLevel(item?.cbot),
      },
      {
        title: 'CME',
        render: (item) => renderLevel(item?.cme),
      },
      {
        title: 'COMEX',
        render: (item) => renderLevel(item?.comex),
      },
      {
        title: 'NYMEX',
        render: (item) => renderLevel(item?.nymex),
      },
      {
        title: 'EUREX',
        render: (item) => renderLevel(item?.eurex),
      },
      {
        title: 'Max Sessions',
        render: (item) => <p>{item?.max_session_data}</p>,
      },
      {
        title: '',
        render: (item) => (
          <div>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => navigate(`/admin/users/${item?.id}`)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div>
      <div className={'text-xs flex flex-col gap-x-8 gap-y-3'}>
        <h1 className={'text-[23px]'}>{`Users with Extra Sessions`}</h1>
        {data?.length ? (
          <p>
            Viewing <b>{data?.length} user(s)</b>
          </p>
        ) : null}
      </div>
      <Card className={'mt-4.5'}>
        <div
          className={
            'flex justify-between items-start lg:flex-row flex-col mb-5 gap-6'
          }
        >
          <div className={'w-full flex gap-6 flex-wrap'}>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created From'}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created To'}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
          <div className={'flex gap-5'}>
            <Button size={'sm'} variant={'lime'} onClick={handleGetData}>
              Get
            </Button>
          </div>
        </div>
        <Divider />
        <RadixTable columns={columns} data={data} />
      </Card>
    </div>
  );
};
